import { useForm } from "react-hook-form";
import styled from "@emotion/styled";
import { Progress } from "antd5";
import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import Input from "components/ui/input/Input";
import Label from "components/ui/input/Label";
import { toast } from "components/ui/toast/Toast";
import { useUpdateCompany } from "lib/hooks/api/company/useUpdateCompany";
import { useAssertCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { useUpdateCurrentUser } from "lib/hooks/api/users/useUpdateCurrentUser";
import { fieldInteractionEvent } from "./tracking";

type ProfileSetupFormData = {
  firstName: string;
  lastName: string;
  companyName: string;
  jobTitle: string;
};

type ProfileSetupProps = {
  onComplete: () => void;
  companyNameEditable: boolean;
};

function ProfileSetup({ onComplete, companyNameEditable }: ProfileSetupProps) {
  const currentUser = useAssertCurrentUser();

  const {
    control,
    handleSubmit,
    formState: { isValid, errors },
  } = useForm<ProfileSetupFormData>({
    defaultValues: {
      firstName: currentUser.firstName,
      lastName: currentUser.lastName,
      companyName: currentUser.company.name,
      jobTitle: currentUser.jobTitle ?? "",
    },
    mode: "onChange",
  });

  const { mutateAsync: updateUser, isLoading: isUpdatingUser } = useUpdateCurrentUser({
    onError: () => {
      toast.error("Failed to set user details.");
    },
  });
  const { mutateAsync: updateCompany, isLoading: isUpdatingCompany } = useUpdateCompany({
    onError: () => {
      toast.error("Failed to set company name.");
    },
  });

  const isLoading = isUpdatingUser || isUpdatingCompany;

  const onSubmit = handleSubmit(async (data) => {
    const results = await Promise.allSettled([
      updateUser({
        request: {
          guid: currentUser.guid,
          firstName: data.firstName,
          lastName: data.lastName,
          jobTitle: data.jobTitle,
        },
      }),

      ...(companyNameEditable
        ? [
            updateCompany({
              input: {
                guid: currentUser.company.guid,
                name: data.companyName,
              },
            }),
          ]
        : []),
    ]);

    if (results.every((r) => r.status === "fulfilled")) {
      onComplete();
    }
  });

  return (
    <form onSubmit={onSubmit}>
      <Flex column gap={40}>
        <ProgressBar>
          <Progress percent={20} showInfo={false} />
        </ProgressBar>
        <Flex column gap={16}>
          <Text h1>Complete your profile</Text>
          <Text>Review your details so your team can find you.</Text>
        </Flex>
        <Flex column gap={16}>
          <Flex column gap={4}>
            <Label htmlFor="firstName">First name</Label>
            <Input<ProfileSetupFormData>
              name="firstName"
              placeholder="Ari"
              control={control}
              rules={{ required: "Please enter your first name" }}
              onChange={() => fieldInteractionEvent("", "firstName")}
            />
            {errors.firstName?.message && (
              <Text color="sysDestructiveDefault">{errors.firstName.message}</Text>
            )}
          </Flex>
          <Flex column gap={4}>
            <Label htmlFor="lastName">Last name</Label>
            <Input<ProfileSetupFormData>
              name="lastName"
              placeholder="Stotle"
              control={control}
              rules={{ required: "Please enter your last name" }}
              onChange={() => fieldInteractionEvent("", "lastName")}
            />
            {errors.lastName?.message && (
              <Text color="sysDestructiveDefault">{errors.lastName.message}</Text>
            )}
          </Flex>
          <Flex column gap={4}>
            <Label htmlFor="companyName">Company name</Label>
            <Input<ProfileSetupFormData>
              name="companyName"
              placeholder="Phylosofi"
              control={control}
              disabled={!companyNameEditable}
              rules={{ required: "Please enter your company name" }}
              onChange={() => fieldInteractionEvent("", "companyName")}
            />
            {errors.companyName?.message && (
              <Text color="sysDestructiveDefault">{errors.companyName.message}</Text>
            )}
          </Flex>
          <Flex column gap={4}>
            <Label htmlFor="jobTitle">Job title</Label>
            <Input<ProfileSetupFormData>
              name="jobTitle"
              placeholder="Account Manager"
              control={control}
              rules={{ required: "Please enter your job title" }}
              onChange={() => fieldInteractionEvent("", "jobTitle")}
            />
            {errors.jobTitle?.message && (
              <Text color="sysDestructiveDefault">{errors.jobTitle.message}</Text>
            )}
          </Flex>
        </Flex>
        <Button disabled={!isValid} loading={isLoading} type="submit" width="fit-content">
          Continue
        </Button>
      </Flex>
    </form>
  );
}

const ProgressBar = styled.div({
  maxWidth: "200px",
  marginBottom: "56px",
});

export default ProfileSetup;
